import { notify } from '@affine/component';
import { DebugLogger } from '@affine/debug';
import {
  getOrCreateI18n,
  i18nCompletenesses,
  type Language,
  SUPPORTED_LANGUAGES,
} from '@affine/i18n';
import { effect, Entity, fromPromise, LiveData } from '@toeverything/infra';
import { catchError, EMPTY, exhaustMap } from 'rxjs';

import type { GlobalCache } from '../../storage';

export type LanguageInfo = {
  key: Language;
  name: string;
  originalName: string;
  completeness: number;
};

const logger = new DebugLogger('i18n');

/** Idioma da interface quando o usuário ainda não escolheu nenhum. */
export const DEFAULT_LANGUAGE: Language = 'pt-BR';

const LANGUAGE_CACHE_KEY = 'i18n_lng';

/**
 * Marcador da migração única 'en' -> 'pt-BR'.
 *
 * Antes deste conserto, `init()` caía em 'en' quando não havia idioma salvo, e o
 * listener `languageChanged` gravava esse 'en' no cache. Quem nunca escolheu
 * inglês ficou preso nele. A migração troca esse 'en' por 'pt-BR' uma única vez;
 * depois do marcador gravado, um 'en' salvo é escolha do usuário e é mantido.
 */
export const LANGUAGE_MIGRATION_CACHE_KEY = 'i18n_lng_migrado_ptbr';

/**
 * Decide o idioma inicial da interface a partir do cache, aplicando a migração
 * única 'en' -> 'pt-BR' quando ela ainda não rodou.
 */
export function resolveInitialLanguage(cache: GlobalCache): Language {
  const saved = cache.get<Language>(LANGUAGE_CACHE_KEY);

  if (!cache.get<boolean>(LANGUAGE_MIGRATION_CACHE_KEY)) {
    cache.set(LANGUAGE_MIGRATION_CACHE_KEY, true);
    if (saved === 'en') {
      cache.set(LANGUAGE_CACHE_KEY, DEFAULT_LANGUAGE);
      return DEFAULT_LANGUAGE;
    }
  }

  return saved ?? DEFAULT_LANGUAGE;
}

function mapLanguageInfo(language: Language = DEFAULT_LANGUAGE): LanguageInfo {
  const languageInfo = SUPPORTED_LANGUAGES[language];

  return {
    key: language,
    name: languageInfo.name,
    originalName: languageInfo.originalName,
    completeness: i18nCompletenesses[language],
  };
}

export class I18n extends Entity {
  private readonly i18n = getOrCreateI18n();

  get i18next() {
    return this.i18n;
  }

  readonly currentLanguageKey$ = LiveData.from(
    this.cache.watch<Language>(LANGUAGE_CACHE_KEY),
    undefined
  );

  readonly currentLanguage$ = this.currentLanguageKey$
    .distinctUntilChanged()
    .map(mapLanguageInfo);

  readonly languageList: Array<LanguageInfo> =
    // @ts-expect-error same key indexing
    Object.keys(SUPPORTED_LANGUAGES).map(mapLanguageInfo);

  constructor(private readonly cache: GlobalCache) {
    super();
    this.i18n.on('languageChanged', (language: Language) => {
      document.documentElement.lang = language;
      this.cache.set(LANGUAGE_CACHE_KEY, language);
    });
  }

  init() {
    this.changeLanguage(resolveInitialLanguage(this.cache));
  }

  changeLanguage = effect(
    exhaustMap((language: string) =>
      fromPromise(() => this.i18n.changeLanguage(language)).pipe(
        catchError(error => {
          notify({
            theme: 'error',
            title: 'Failed to change language',
            message: 'Error occurs when loading language files',
          });

          logger.error('Failed to change language', error);

          return EMPTY;
        })
      )
    )
  );
}
